/* POVA — media viewport playback (site-wide)
 *
 * Every decorative <video> on the site plays ONLY while its frame is on screen.
 * Nothing auto-plays or downloads on page load; a clip starts the moment it
 * scrolls into view and pauses + rewinds when it leaves, so it always begins
 * from the top the next time you land on it.
 *
 * SCOPE: applies to every <video> that is NOT a user-controlled player. Add the
 * `controls` attribute (a real player) or `data-video-manual` to opt a video
 * out and manage it yourself. The bespoke product films carry `data-xp-video`,
 * but this engine governs any qualifying video on any page — present or future
 * — because it is loaded globally from layout/theme.liquid.
 *
 * AUDIO follows the site's one persistent sound switch (body.sound-on): a clip
 * plays muted when sound is off and unmuted when it is on, so scroll-in playback
 * always stays within browser autoplay policy.
 *
 * REDUCED MOTION (WCAG 2.2.2 / 2.3.3): when the user has
 * prefers-reduced-motion: reduce, nothing auto-plays on scroll-into-view — a
 * video sits on its poster / first frame instead.
 */
(function () {
  "use strict";

  var reduceMotion = !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);

  function soundOn() { return document.body && document.body.classList.contains("sound-on"); }

  function prep(v) {
    if (v.dataset.povaMediaWired) return null;
    v.dataset.povaMediaWired = "1";
    /* Never let a video auto-fire on load, even if markup left autoplay on. */
    v.removeAttribute("autoplay");
    if (!v.hasAttribute("muted")) v.muted = true;
    return v;
  }

  function play(v) {
    v.muted = !soundOn();
    var p = v.play();
    if (p && p.catch) p.catch(function () {
      /* Unmuted play blocked by policy — fall back to muted so it still shows. */
      v.muted = true;
      var r = v.play();
      if (r && r.catch) r.catch(function () {});
    });
  }

  function collect() {
    return [].slice.call(document.querySelectorAll("video:not([controls]):not([data-video-manual])"));
  }

  /* Locale-driven labels (shared window.POVA_I18N, set in layout/theme.liquid). */
  function t(k, d) { try { return (window.POVA_I18N && window.POVA_I18N[k] != null) ? window.POVA_I18N[k] : d; } catch (e) { return d; } }
  var ICON_PAUSE = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>';
  var ICON_PLAY  = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>';

  /* WCAG 2.2.2 Pause, Stop, Hide: give every auto-playing decorative video a visible,
     keyboard-operable Play/Pause control. A user pause is remembered (__userPaused) so
     the scroll observer never silently resumes it. Labels reflect the action and come
     from the shared locale strings. */
  function decorate(v) {
    if (v.__povaToggle) return;
    var host = v.parentNode;
    if (!host || !host.appendChild) return;
    if (host.querySelector && host.querySelector(".pova-media-toggle")) return;
    try { if (getComputedStyle(host).position === "static") host.style.position = "relative"; } catch (e) {}
    var btn = document.createElement("button");
    btn.type = "button";
    btn.className = "pova-media-toggle";
    v.__povaToggle = btn;
    function reflect() {
      var playing = !v.paused && !v.ended;
      btn.innerHTML = playing ? ICON_PAUSE : ICON_PLAY;
      btn.setAttribute("aria-label", playing ? t("media_pause", "Pause video") : t("media_play", "Play video"));
    }
    btn.addEventListener("click", function (e) {
      e.preventDefault(); e.stopPropagation();
      if (v.paused) { v.__userPaused = false; play(v); }
      else { v.__userPaused = true; v.pause(); }
    });
    v.addEventListener("play", reflect);
    v.addEventListener("pause", reflect);
    reflect();
    host.appendChild(btn);
  }

  function ready(fn) { document.readyState !== "loading" ? fn() : document.addEventListener("DOMContentLoaded", fn); }

  ready(function () {
    var vids = collect().map(prep).filter(Boolean);
    if (!vids.length) return;

    /* Every managed (auto-playing) video gets a Play/Pause control (WCAG 2.2.2). */
    vids.forEach(decorate);

    if (reduceMotion) {
      /* Reduced motion: never auto-play on scroll. Show the poster / first
       * frame only — load just enough to paint it when there's no poster. The
       * Play control lets a user opt in; mark paused so nothing resumes it. */
      vids.forEach(function (v) {
        if (!v.hasAttribute("poster")) v.preload = "metadata";
        v.__userPaused = true;
        v.pause();
      });
      return;
    }

    if (!("IntersectionObserver" in window)) {
      /* No observer support — degrade to muted playback so the media still shows. */
      vids.forEach(function (v) { v.muted = true; play(v); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var v = e.target;
        if (e.isIntersecting) {
          if (!v.__userPaused) play(v); // respect a user pause — don't silently resume
        } else {
          v.pause();
          try { v.currentTime = 0; } catch (err) {}
        }
      });
    }, { threshold: 0.35 });

    vids.forEach(function (v) { io.observe(v); });
  });
})();
